import { autoinject } from "aurelia-framework";
import { FetchClient } from "../common/fetch-client";
import { ApplicationEnum } from "../enums/enums";

@autoinject()
export class AxxessIntelligenceService {
    private _httpClient: FetchClient;
    private _premiumUrl: string = "/api/v1/premium";
    private premiumStatusCache: Map<string, boolean> = new Map();

    public constructor(httpClient: FetchClient) {
        this._httpClient = httpClient;
    }

    public async getAiPermissionByAgencies(agencyIds: IGetAiPermissionAgenciespayload) {
        // if any agency is already known to be premium, skip the API call
        if (this.premiumStatusCache.size > 0) {
            const hasCachedPremium = agencyIds.AgencyIds.some(
                (agencyId) => this.premiumStatusCache.get(agencyId) === true
            );
            if (hasCachedPremium) {
                return {
                    IsPremium: true,
                    Message: "Agency is premium"
                } as IGetAiPermissionByAgenciesResp;
            }
        }

        const response = await this._fetchPremiumStatus(agencyIds);

        if (agencyIds.AgencyIds.length == 1) {
            this.premiumStatusCache.set(agencyIds.AgencyIds[0], response.IsPremium);
        }

        return response;
    }

    public async getAiPermissionOfAgencies(agencyIds: IGetAiPermissionAgenciespayload) {
        // split into agencies already cached and ones that still need to be fetched
        const premiumStatuses: { [agencyId: string]: boolean } = {};
        const missingAgencyIds: string[] = [];
        agencyIds.AgencyIds.forEach((agencyId) => {
            if (this.premiumStatusCache.has(agencyId)) {
                premiumStatuses[agencyId] = this.premiumStatusCache.get(agencyId);
            } else {
                missingAgencyIds.push(agencyId);
            }
        });

        if (missingAgencyIds.length === 0) {
            return {
                PremiumStatuses: premiumStatuses,
                Message: "Agency premium statuses"
            } as IGetAiPermissionOfAgenciesResp;
        }

        const response = await this._fetchPremiumStatuses({ AgencyIds: missingAgencyIds });

        Object.keys(response.PremiumStatuses).forEach((agencyId) => {
            this.premiumStatusCache.set(agencyId, response.PremiumStatuses[agencyId]);
            premiumStatuses[agencyId] = response.PremiumStatuses[agencyId];
        });

        return {
            PremiumStatuses: premiumStatuses,
            Message: response.Message
        } as IGetAiPermissionOfAgenciesResp;
    }

    private async _fetchPremiumStatus(agencyIds: IGetAiPermissionAgenciespayload) {
        return await this._httpClient
            .build(`${this._premiumUrl}/status`)
            .useMethod("POST")
            .useBody(agencyIds)
            .fetch<IGetAiPermissionByAgenciesResp>();
    }

    private async _fetchPremiumStatuses(agencyIds: IGetAiPermissionAgenciespayload) {
        return await this._httpClient
            .build(`${this._premiumUrl}/premium/statuses`)
            .useMethod("POST")
            .useBody(agencyIds)
            .fetch<IGetAiPermissionOfAgenciesResp>();
    }
}

export interface IGetAiPermissionByAgenciesResp {
    IsPremium: boolean;
    Message: string;
}

export interface IGetAiPermissionOfAgenciesResp {
    PremiumStatuses: {
        [agencyId: string]: boolean;
    };
    Message: string;
}

export interface IGetAiPermissionAgenciespayload {
    AgencyIds: string[];
}
